import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Plus, RefreshCw, Search } from 'lucide-react';
import clsx from 'clsx';
import { devicesApi, type DeviceListEntry } from '@/lib/api';
import { AddWirelessDebugModal } from '@/components/AddWirelessDebugModal';
import { DeviceCard } from '@/components/DeviceCard';

/**
 * 设备列表页。
 *
 * 本机直连设备默认全用户可达 — 所有 owner / visibility / claim UI
 * 在 share-cleanup pass 已经移除。设备统一显示在一张 grid 上，排序按
 * "在线 → 离线 → fake 沉底" 两层。
 */
export default function Devices() {
  const qc = useQueryClient();
  const [pairModalOpen, setPairModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
    refetchInterval: 5_000,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const list: DeviceListEntry[] = useMemo(() => data?.devices ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) => {
      const hay = [d.serial, d.alias, d.model, d.product].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [list, search]);

  // 单一排序：在线 > 离线 > fake。fake 沉底是因为 adb 操作对它们无效，
  // 用户从列表里挑设备时不应被它们干扰；离线物理设备夹中间，因为它们
  // 仍可能恢复在线。
  const sortDevices = useCallback(
    (a: DeviceListEntry, b: DeviceListEntry): number => {
      const bucket = (d: DeviceListEntry): number => {
        if (d.kind === 'fake') return 2;
        return d.state === 'device' ? 0 : 1;
      };
      return bucket(a) - bucket(b);
    },
    []
  );

  const sorted = useMemo(() => [...filtered].sort(sortDevices), [filtered, sortDevices]);

  // Active refetch (not invalidate) so we only refresh queries mounted
  // right now, and we re-fetch immediately instead of letting react-query
  // schedule a background refresh that could silently overwrite the
  // optimistic `patchDeviceCache` row-replace from `DeviceCard`.
  function invalidate(): void {
    void qc.refetchQueries({ queryKey: ['devices'], type: 'active' });
  }

  return (
    <div className="space-y-6 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">设备 · Devices</h1>
          <p className="mt-0.5 text-xs text-ink-muted">
            插上即可见 —— 点设备卡片上的任意功能图标直接进入，无需先「进入」再操作。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-dim" />
            <input
              className="input w-56 pl-8"
              placeholder="搜索 别名 / 型号 / serial"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className="btn-primary"
            onClick={() => setPairModalOpen(true)}
            title="通过 QR 扫码 + Wi-Fi 配对一台新设备"
          >
            <Plus className="h-4 w-4" />
            <span>添加无线设备</span>
          </button>
          <button
            className="btn-ghost"
            onClick={() => refetch()}
            disabled={isFetching}
            title="刷新设备列表（每 5s 自动刷新）"
          >
            <RefreshCw className={clsx('h-4 w-4', isFetching && 'animate-spin')} />
            <span className="hidden sm:inline">刷新</span>
          </button>
        </div>
      </header>

      <DeviceGrid
        devices={sorted}
        emptyHint={
          search
            ? '没有匹配的设备 —— 调整搜索词或清空搜索框查看全部。'
            : '没有发现 Android 设备 —— 用数据线插一台，或点右上「添加无线设备」扫码配对。'
        }
        onMutate={invalidate}
      />

      <AddWirelessDebugModal
        open={pairModalOpen}
        onClose={() => {
          setPairModalOpen(false);
          invalidate();
        }}
      />
    </div>
  );
}

interface DeviceGridProps {
  devices: DeviceListEntry[];
  emptyHint: string;
  onMutate: () => void;
}

/**
 * 设备 grid。auto-fill 每张卡 ~280 px，容器有多宽就排多少列，单栏
 * 也不会出现奇数空白格的尴尬。
 */
function DeviceGrid({ devices, emptyHint, onMutate }: DeviceGridProps) {
  return (
    <section className="card flex flex-col overflow-hidden">
      <header className="border-b border-line bg-bg-soft px-4 py-3">
        <h2 className="text-sm font-semibold tracking-tight text-ink">已连接设备</h2>
        <p className="mt-1 text-[10px] uppercase tracking-wide text-ink-dim">
          {devices.length} devices
        </p>
      </header>
      {devices.length === 0 ? (
        <div className="px-4 py-10 text-center text-xs text-ink-muted">
          <CircleAlert className="mx-auto mb-1 h-4 w-4 text-ink-dim" />
          {emptyHint}
        </div>
      ) : (
        <div className="grid auto-rows-fr grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-px bg-line [&>*]:bg-bg-card">
          {devices.map((d) => (
            <DeviceCard key={d.serial} device={d} onMutate={onMutate} />
          ))}
        </div>
      )}
    </section>
  );
}
